/**
 * Applies DK code discounts on PDP/PLP when codes live in cookies (Shopify discount_code / arn_dk_active_codes).
 * Liquid cannot read those cookies; server HTML is patched client-side.
 */
(function () {
  const ACTIVE_COOKIE = 'arn_dk_active_codes';
  const SHOPIFY_CODE_COOKIES = ['discount_code', 'discount'];

  const normalizeCode = (value) => String(value || '').trim().toLowerCase();

  const getCookie = (name) => {
    const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const match = document.cookie.match(new RegExp(`(?:^|; )${escaped}=([^;]*)`));
    return match ? decodeURIComponent(match[1]) : '';
  };

  const readActiveCookieCodes = () => {
    const raw = getCookie(ACTIVE_COOKIE);
    if (!raw) return [];
    return raw.split(',').map(normalizeCode).filter(Boolean);
  };

  const setActiveCookieCodes = (codes) => {
    const unique = [...new Set(codes.map(normalizeCode).filter(Boolean))];
    if (!unique.length) return;
    const maxAge = 60 * 60 * 24 * 7;
    document.cookie = `${ACTIVE_COOKIE}=${encodeURIComponent(unique.join(','))}; path=/; max-age=${maxAge}; SameSite=Lax`;
  };

  const mergeCodes = (...lists) => {
    const codes = new Set();
    lists.flat().forEach((value) => {
      const normalized = normalizeCode(value);
      if (normalized) codes.add(normalized);
    });
    return Array.from(codes);
  };

  const getAppliedCodesCsv = () => {
    const fromDataset = document.documentElement?.dataset?.arnDkAppliedCodes || '';
    if (fromDataset) return fromDataset.startsWith(',') ? fromDataset : `,${fromDataset},`;

    const codes = mergeCodes(
      SHOPIFY_CODE_COOKIES.map(getCookie).filter(Boolean),
      readActiveCookieCodes()
    );

    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get('discount')) codes.push(...mergeCodes(params.get('discount')));
      if (params.get('arn_dk_codes')) codes.push(...mergeCodes(params.get('arn_dk_codes').split(',')));
    } catch {
      /* ignore */
    }

    return codes.length ? `,${codes.join(',')},` : '';
  };

  const getStorefrontContext = () => {
    const ctx = window.arnDkStorefrontContext || {};
    return {
      market: String(ctx.market || '').toLowerCase().trim(),
      currency: String(ctx.currency || '').toUpperCase().trim(),
      appliedCodes: getAppliedCodesCsv(),
      customerHasTestTag: Boolean(ctx.customerHasTestTag),
    };
  };

  const parseDiscounts = (raw) => {
    if (!raw) return [];
    try {
      const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  };

  const parseMaxDiscount = (raw) => {
    if (!raw) return null;
    try {
      const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : null;
    } catch {
      return null;
    }
  };

  const hasItems = (value) => Array.isArray(value) && value.length > 0;

  const LINE_LEVEL_EXCLUDED_TYPES = new Set([
    'ORDER_GOAL',
    'GWP',
    'BXGY',
    'FREE_SHIPPING',
    'SHIPPING',
    'UNKNOWN_APP',
  ]);

  const isUniversalProductTargeting = (disc) => {
    if (!disc || typeof disc !== 'object') return false;
    if (LINE_LEVEL_EXCLUDED_TYPES.has(disc.discount_type)) return false;

    if (
      hasItems(disc.reward_product_references) ||
      hasItems(disc.reward_collection_references) ||
      hasItems(disc.threshold_product_references) ||
      hasItems(disc.threshold_collection_references)
    ) {
      return false;
    }

    const buckets = disc.config?.bucketRules;
    if (Array.isArray(buckets)) {
      for (const bucket of buckets) {
        if (hasItems(bucket?.collectionFilter) || hasItems(bucket?.productFilter)) {
          return false;
        }
      }
    }

    return true;
  };

  const getShopDiscountsRaw = () => {
    const el = document.getElementById('arn-dk-shop-discounts');
    if (el && el.textContent) return el.textContent;
    return window.arnDkStorefrontContext?.shopDiscounts || null;
  };

  let _universalShopDiscountsCache = null;
  const getUniversalShopDiscounts = () => {
    if (_universalShopDiscountsCache) return _universalShopDiscountsCache;
    _universalShopDiscountsCache = parseDiscounts(getShopDiscountsRaw()).filter(
      isUniversalProductTargeting
    );
    return _universalShopDiscountsCache;
  };

  const resolveDiscountsFromDataset = (dataset) => {
    const productDiscounts = parseDiscounts(dataset?.arnDkDiscounts);
    if (productDiscounts.length) return productDiscounts;
    const universalShop = getUniversalShopDiscounts();
    if (universalShop.length) return universalShop;
    const maxDisc = parseMaxDiscount(dataset?.arnDkMaxDiscount);
    return maxDisc ? [maxDisc] : [];
  };

  const flattenMarketHandles = (values) => {
    if (values == null) return [];
    if (!Array.isArray(values)) {
      const one = String(values).toLowerCase().trim();
      return one ? [one] : [];
    }
    return values
      .flat(Infinity)
      .map((m) => String(m).toLowerCase().trim())
      .filter(Boolean);
  };

  const dkIncludedMarketsCsv = (disc, market) => {
    const included = flattenMarketHandles(disc.included_markets);
    if (!included.length) return '';
    return included.includes(market) ? `,${included.join(',')}` : '__no_match__';
  };

  const isDkTestMode = (disc) => {
    const t = disc?.test;
    return t === true || t === 'true' || t === 1;
  };

  const getEligibilityFailure = (disc, market, shopCurrency, ctx) => {
    if (isDkTestMode(disc) && !ctx.customerHasTestTag) return 'test';

    const incCsv = dkIncludedMarketsCsv(disc, market);
    if (incCsv === '__no_match__') return 'market';

    const excluded = flattenMarketHandles(disc.excluded_markets);
    if (excluded.includes(market)) return 'excluded_market';

    const discCurrency = disc.currency || disc.config?.currencyCode;
    if (discCurrency && String(discCurrency).toUpperCase() !== shopCurrency) {
      return 'currency';
    }

    return null;
  };

  const isDkDiscountEligible = (disc, market, shopCurrency, ctx) =>
    !getEligibilityFailure(disc, market, shopCurrency, ctx);

  const dkSaleCents = (priceCents, disc) => {
    const mv = Number(disc.maximum_reward_value) || 0;
    let pct = 0;
    let cents = 0;

    if (disc.reward_type === 'PERCENTAGE') {
      pct = mv;
    } else if (disc.reward_type === 'FIXED_AMOUNT') {
      cents = mv;
    } else if (String(disc.discount_type || '').includes('PERCENTAGE')) {
      pct = mv;
    } else if (mv > 0) {
      cents = mv;
    }

    if (pct > 0 && pct < 100) {
      return Math.round((priceCents * (100 - pct)) / 100);
    }
    if (cents > 0 && cents < priceCents) {
      return priceCents - cents;
    }
    return priceCents;
  };

  const bestDkSaleCents = (priceCents, discounts, ctx) => {
    const { market, currency: shopCurrency, appliedCodes } = ctx;
    let bestSavings = 0;
    let bestSale = priceCents;

    for (const disc of discounts) {
      if (!isDkDiscountEligible(disc, market, shopCurrency, ctx)) continue;

      let use = false;
      if (disc.method === 'automatic' || !disc.method) {
        use = true;
      } else if (disc.method === 'code') {
        const code = normalizeCode(disc.code);
        const title = normalizeCode(disc.discount_title);
        if (code && appliedCodes.includes(`,${code},`)) use = true;
        if (!use && title && appliedCodes.includes(`,${title},`)) use = true;
      }
      if (!use) continue;

      const saleCents = dkSaleCents(priceCents, disc);
      const savings = priceCents - saleCents;
      if (savings > bestSavings) {
        bestSavings = savings;
        bestSale = saleCents;
      }
    }

    return { savings: bestSavings, saleCents: bestSale, hasDiscount: bestSavings > 0 };
  };

  const formatCents = (cents, currencyCode) => {
    const value = cents / 100;
    const currency = currencyCode || window.Shopify?.currency?.active || 'USD';
    const locale =
      window.arnDkStorefrontContext?.localeIso ||
      window.Shopify?.locale ||
      document.documentElement.lang ||
      'en';

    try {
      return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(value);
    } catch {
      return `${currency} ${value.toFixed(2)}`;
    }
  };

  const renderPriceHtml = (el, priceCents, compareCents, context) => {
    const currency = el.dataset.arnDkCurrency || window.arnDkStorefrontContext?.currency || '';
    const pct =
      compareCents > priceCents
        ? Math.round(((compareCents - priceCents) / compareCents) * 100)
        : 0;
    const sale = formatCents(priceCents, currency);
    const compare = formatCents(compareCents, currency);

    if (context === 'plp') {
      if (compareCents <= priceCents) {
        el.innerHTML = `<div class="tw-typo-arn-base">${sale}</div>`;
        return;
      }
      el.innerHTML = `
        <div class="tw-typo-arn-base">${sale}</div>
        <div class="tw-typo-arn-sm tw-line-through">${compare}</div>
        <div class="tw-typo-arn-xs tw-uppercase tw-bg-arn-brand dark:tw-text-arn-default tw-px-sm tw-py-xs tw-order-last tb:tw-order-none">${pct}% OFF</div>`;
      return;
    }

    if (compareCents <= priceCents) {
      el.innerHTML = `<span class="arn-product-rich-card-price tw-typo-arn-h3">${sale}</span>`;
      return;
    }

    el.innerHTML = `
      <span class="arn-product-rich-card-price tw-typo-arn-h3">${sale}</span>
      <div class="tw-gap-arn-spacings-md tw-flex">
        <span class="arn-product-rich-card-compare tw-line-through tw-typo-arn-base">${compare}</span>
        <div class="tw-arn-badge tw-arn-badge-primary"><span>${pct}% OFF</span></div>
      </div>`;
  };

  const resolveForElement = (el, ctx) => {
    if (el.dataset.arnDkManualSale === 'true') return null;

    const baseCents = parseInt(el.dataset.arnDkBaseCents, 10);
    if (!baseCents || baseCents <= 0) return null;

    const discounts = resolveDiscountsFromDataset(el.dataset);
    if (!discounts.length) return null;

    const best = bestDkSaleCents(baseCents, discounts, ctx);
    if (!best.hasDiscount) return null;

    return { priceCents: best.saleCents, compareCents: baseCents };
  };

  const publishAppliedCodes = (csv) => {
    const previous = document.documentElement.dataset.arnDkAppliedCodes || '';
    document.documentElement.dataset.arnDkAppliedCodes = csv;
    if (csv !== previous) {
      document.dispatchEvent(
        new CustomEvent('arn:dk:discount-codes-changed', { detail: { appliedCodes: csv } })
      );
    }
  };

  const syncAppliedCodesFromCookies = () => {
    const codes = mergeCodes(
      SHOPIFY_CODE_COOKIES.map(getCookie).filter(Boolean),
      readActiveCookieCodes()
    );

    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get('discount')) codes.push(...mergeCodes(params.get('discount')));
      if (params.get('arn_dk_codes')) codes.push(...mergeCodes(params.get('arn_dk_codes').split(',')));
    } catch {
      /* ignore */
    }

    if (!codes.length) return getAppliedCodesCsv();

    setActiveCookieCodes(codes);
    const csv = `,${codes.join(',')},`;
    publishAppliedCodes(csv);
    return csv;
  };

  const refreshWithin = (root = document) => {
    const ctx = getStorefrontContext();
    const scope = root && root !== document ? root : document;
    const priceNodes = scope.querySelectorAll
      ? scope.querySelectorAll('[data-arn-price][data-arn-dk-base-cents]')
      : [];

    priceNodes.forEach((el) => {
      const resolved = resolveForElement(el, ctx);
      if (!resolved) return;

      const context = el.dataset.arnPriceContext || '';
      renderPriceHtml(el, resolved.priceCents, resolved.compareCents, context);
    });
  };

  const refresh = () => refreshWithin(document);

  const resolveDisplayFromNode = (node) => {
    if (!node?.dataset) return null;

    const baseCents = parseInt(node.dataset.arnDkBaseCents, 10);
    const currency =
      node.dataset.arnDkCurrency || window.arnDkStorefrontContext?.currency || '';

    if (!baseCents || baseCents <= 0) {
      return {
        priceText: node.dataset.price || '',
        compareText: node.dataset.compare || '',
        promoBadge: node.dataset.promoBadge?.trim() || '',
        hasDiscount: Boolean(node.dataset.compare),
      };
    }

    const discounts = resolveDiscountsFromDataset(node.dataset);
    if (!discounts.length) {
      return {
        priceText: node.dataset.price || '',
        compareText: node.dataset.compare || '',
        promoBadge: node.dataset.promoBadge?.trim() || '',
        hasDiscount: Boolean(node.dataset.compare),
      };
    }

    const ctx = getStorefrontContext();
    const best = bestDkSaleCents(baseCents, discounts, ctx);

    if (!best.hasDiscount) {
      return {
        priceText: formatCents(baseCents, currency),
        compareText: '',
        promoBadge: '',
        hasDiscount: false,
      };
    }

    const pct = Math.round(((baseCents - best.saleCents) / baseCents) * 100);
    return {
      priceText: formatCents(best.saleCents, currency),
      compareText: formatCents(baseCents, currency),
      promoBadge: `${pct}% OFF`,
      hasDiscount: true,
    };
  };

  window.ArnDkStorefrontPrices = {
    refresh,
    refreshWithin,
    syncAppliedCodesFromCookies,
    getAppliedCodesCsv,
    resolveDisplayFromNode,
    bestDkSaleCents,
    parseDiscounts,
    resolveDiscountsFromDataset,
    isUniversalProductTargeting,
    getUniversalShopDiscounts,
  };

  const run = () => {
    syncAppliedCodesFromCookies();
    refresh();
  };

  const scheduleRetries = () => {
    [300, 800, 1500].forEach((ms) => {
      window.setTimeout(() => {
        syncAppliedCodesFromCookies();
        refresh();
      }, ms);
    });
  };

  const syncFromCart = async () => {
    const root = window.Shopify?.routes?.root || '/';
    try {
      const response = await fetch(`${root}cart.js`);
      if (!response.ok) return;
      const cart = await response.json();
      const codes = [];
      (cart?.discount_codes || []).forEach((entry) => {
        if (entry?.code) codes.push(entry.code);
      });
      (cart?.discount_applications || []).forEach((app) => {
        if (app?.type === 'discount_code') {
          if (app.code) codes.push(app.code);
          if (app.title) codes.push(app.title);
        }
      });
      if (!codes.length) return;
      setActiveCookieCodes(mergeCodes(codes, readActiveCookieCodes()));
      publishAppliedCodes(getAppliedCodesCsv());
      refresh();
    } catch {
      /* ignore */
    }
  };
  window.ArnDkStorefrontPrices.syncFromCart = syncFromCart;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      run();
      scheduleRetries();
      syncFromCart();
    });
  } else {
    run();
    scheduleRetries();
    syncFromCart();
  }

  document.addEventListener('arn:dk:discount-codes-changed', refresh);
})();
